// oxlint-disable react/no-array-index-key
import { PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getEvaluationCore } from 'types';
import type { EvaluationParams, EvaluationRowResult, EvaluationView } from 'types';

import { useChecklistContext } from '../ChecklistContext';
import { FlagAlertBadge } from '../ChecklistInputFlagAlertBadge';
import type { EvaluationInputComponentProps } from '../evaluationClientRegistry';
import { computeLiveEvaluationState } from '../evaluationLiveCompute';

const roundForDisplay = (value: number, decimals = 1): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const enteredRowsSignature = (view: EvaluationView | undefined): string =>
  JSON.stringify(view?.rows.map((row) => row.entered.installedPitch ?? null) ?? []);

const getInitialEnteredValues = (view: EvaluationView | undefined): Array<number | null> =>
  view?.rows.map((row) => row.entered.installedPitch ?? null) ?? [];

interface ArrayPitchRowProps {
  index: number;
  expectedPitch: number | undefined;
  installedPitch: number | null;
  committedInstalledPitch: number | null;
  result: EvaluationRowResult | undefined;
  params: EvaluationParams;
  readOnly: boolean;
  onChange: (index: number, value: number | null) => void;
  onBlur: (index: number) => void;
}

const ArrayPitchRow = ({
  index,
  expectedPitch,
  installedPitch,
  committedInstalledPitch,
  result,
  params,
  readOnly,
  onChange,
  onBlur,
}: ArrayPitchRowProps) => {
  const expectedPitchDisplay = expectedPitch !== undefined ? `${roundForDisplay(expectedPitch)}°` : '—';
  const tolerance =
    result?.lowerBound !== undefined && result?.upperBound !== undefined && expectedPitch !== undefined
      ? result.upperBound - expectedPitch
      : undefined;
  const difference = result?.difference;
  const differenceDisplay =
    difference !== undefined ? `${difference >= 0 ? '+' : ''}${roundForDisplay(difference)}°` : undefined;
  const verdict = result?.verdict ?? 'incomplete';
  const isBelowMinimumDegrees =
    committedInstalledPitch !== null &&
    params.minDegrees !== undefined &&
    committedInstalledPitch < params.minDegrees;
  const isAboveMaximumDegrees =
    committedInstalledPitch !== null &&
    params.maxDegrees !== undefined &&
    committedInstalledPitch > params.maxDegrees;

  let detail;

  if (verdict === 'pass' && differenceDisplay !== undefined && tolerance !== undefined) {
    detail = (
      <>
        {differenceDisplay} —{' '}
        <span className="text-banner-text-primary-success-default">within ±{roundForDisplay(tolerance)}°</span>
      </>
    );
  } else if (verdict === 'fail' && isBelowMinimumDegrees) {
    detail = <span className="text-banner-text-primary-error-default">below minimum {params.minDegrees}°</span>;
  } else if (verdict === 'fail' && isAboveMaximumDegrees) {
    detail = <span className="text-banner-text-primary-error-default">above maximum {params.maxDegrees}°</span>;
  } else if (verdict === 'fail' && differenceDisplay !== undefined && tolerance !== undefined) {
    detail = (
      <>
        {differenceDisplay} —{' '}
        <span className="text-banner-text-primary-error-default">outside ±{roundForDisplay(tolerance)}°</span>
      </>
    );
  } else {
    detail = 'Measured pitch not entered';
  }

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const parsed = raw === '' ? null : Number(raw);
      onChange(index, parsed === null || Number.isNaN(parsed) ? null : parsed);
    },
    [index, onChange],
  );

  const handleBlur = useCallback(() => {
    onBlur(index);
  }, [index, onBlur]);

  return (
    <div className="border-border-separator flex items-center justify-between gap-16 border-b py-8">
      <div className="w-50 flex flex-col">
        <div className="body-bold-300 text-text-primary">
          Array {index + 1} Expected Pitch: {expectedPitchDisplay}
        </div>
        <div className="body-100 text-text-secondary">{detail}</div>
      </div>

      <div className="flex-1">
        <PalmettoTextInput
          id={`evaluation-arrayPitch-installed-${index}`}
          label={`Measured`}
          type="number"
          value={installedPitch ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={readOnly}
          suffix="°"
        />
      </div>
    </div>
  );
};

export const ArrayPitchEvaluationInput: React.FC<EvaluationInputComponentProps> = ({ view, readOnly, onChange }) => {
  const { accountId } = useChecklistContext();
  const onChangeRef = useRef(onChange);
  const [enteredValues, setEnteredValues] = useState<Array<number | null>>(() => getInitialEnteredValues(view));
  const [committedValues, setCommittedValues] = useState<Array<number | null>>(() => getInitialEnteredValues(view));
  const [seededSignature, setSeededSignature] = useState(() => enteredRowsSignature(view));

  const currentSignature = enteredRowsSignature(view);

  useEffect(() => {
    if (currentSignature !== seededSignature) {
      const nextValues = getInitialEnteredValues(view);
      setEnteredValues(nextValues);
      setCommittedValues(nextValues);
      setSeededSignature(currentSignature);
    }
  }, [currentSignature, seededSignature, view]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (readOnly) return;

    if (view?.hasRequiredData === false) {
      onChangeRef.current({
        key: view.key,
        rows: [],
        verdict: 'incomplete',
      });
    }
  }, [view?.hasRequiredData, view?.key, readOnly]);

  const core = view ? getEvaluationCore(view.key) : undefined;

  const rowResults = useMemo(() => {
    if (!view || !core) return [];

    return view.rows.map((row, index) =>
      core.computeRow(row.derived, { ...row.entered, installedPitch: committedValues[index] ?? null }, view.params),
    );
  }, [view, core, committedValues]);

  const verdict = useMemo((): 'pass' | 'fail' | 'incomplete' => {
    if (rowResults.some((r) => r.verdict === 'fail')) return 'fail';
    if (rowResults.length > 0 && rowResults.every((r) => r.verdict === 'pass')) return 'pass';
    return 'incomplete';
  }, [rowResults]);

  const handleRowChange = useCallback(
    (index: number, value: number | null) => {
      setEnteredValues((current) => view?.rows.map((_row, i) => (i === index ? value : (current[i] ?? null))) ?? []);
    },
    [view],
  );

  const handleRowBlur = useCallback(
    (index: number) => {
      if (!view) return;

      const nextCommitted = view.rows.map((_row, i) =>
        i === index ? (enteredValues[index] ?? null) : (committedValues[i] ?? null),
      );
      const enteredByRow = view.rows.map((row, i) => ({ ...row.entered, installedPitch: nextCommitted[i] }));

      setCommittedValues(nextCommitted);
      onChange(computeLiveEvaluationState(view, enteredByRow));
    },
    [view, enteredValues, committedValues, onChange],
  );

  const failureType = useMemo((): 'bounds' | 'tolerance' | 'mixed' | null => {
    if (verdict !== 'fail') return null;
    const failingRows = rowResults.filter((r) => r.verdict === 'fail');
    const hasBounds = failingRows.some((r) => r.difference === undefined);
    const hasTolerance = failingRows.some((r) => r.difference !== undefined);
    if (hasBounds && hasTolerance) return 'mixed';
    return hasBounds ? 'bounds' : 'tolerance';
  }, [verdict, rowResults]);

  const badgeMessage =
    verdict === 'pass'
      ? 'Within tolerance'
      : failureType === 'bounds'
        ? 'Outside absolute bounds'
        : failureType === 'tolerance'
          ? 'Outside tolerance'
          : verdict === 'fail'
            ? 'Pitch requirements not met'
            : 'Data unavailable';

  if (!view) return null;

  if (view.hasRequiredData === false) {
    return (
      <div
        role="alert"
        className="flex flex-col gap-4 rounded-8 border border-banner-border-warning-default bg-banner-canvas-warning-default p-12 body-300 text-banner-text-primary-warning-default"
      >
        <span>Array data must be populated in System Design before completing this item.</span>
        {accountId && (
          <a
            href={`/accounts/${accountId}/system-design`}
            rel="noreferrer"
            className="text-banner-text-primary-warning-default underline"
          >
            Go to System Design
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {view.rows.map((row, index) => (
        <ArrayPitchRow
          key={index}
          index={index}
          expectedPitch={row.derived.expectedPitch}
          installedPitch={enteredValues[index] ?? null}
          committedInstalledPitch={committedValues[index] ?? null}
          result={rowResults[index]}
          params={view.params}
          readOnly={readOnly}
          onChange={handleRowChange}
          onBlur={handleRowBlur}
        />
      ))}

      <div className="pt-8">
        <FlagAlertBadge verdict={verdict} message={badgeMessage} />
      </div>
    </div>
  );
};
